import config from "appConfig";

const theory = ` <h3 class=sectionHead>Radiation of a flat surface in an infinite screen</h3>
<p>
   We consider a surface
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>S</mi>
   </math>
   in an infinite plane of which each point of coordinate
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msup>
         <mrow>
            <mi>r</mi>
         </mrow>
         <mrow>
            <mi>′</mi>
         </mrow>
      </msup>
   </math>
   oscillates at the speed
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>v</mi>
         </mrow>
         <mrow>
            <mi>n</mi>
         </mrow>
      </msub>
      <mo class=MathClass-open>(</mo>
      <msup>
         <mrow>
            <mi>r</mi>
         </mrow>
         <mrow>
            <mi>′</mi>
         </mrow>
      </msup>
      <mo class=MathClass-close>)</mo>
   </math>. Each point can be considered as a point source and we can write
<table class=equation>
   <tr>
      <td style="font-size: 14px;">
         <math display=block xmlns=http://www.w3.org/1998/Math/MathML class=equation>
            <mi>p</mi>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi>r</mi>
            <mo class=MathClass-close stretchy=false>)</mo>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mi mathvariant=italic>jk</mi>
                  <msub>
                     <mrow>
                        <mi>ρ</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
                  <mi>c</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
                  <mi>π</mi>
               </mrow>
            </mfrac>
            <msub>
               <mrow>
                  <mo>∬</mo>
               </mrow>
               <mrow>
                  <mi>S</mi>
               </mrow>
            </msub>
            <mfrac>
               <mrow>
                  <msup>
                     <mrow>
                        <mi>e</mi>
                     </mrow>
                     <mrow>
                        <mo class=MathClass-bin>−</mo>
                        <mi mathvariant=italic>jk</mi>
                        <mo class=MathClass-rel>|</mo>
                        <mi>r</mi>
                        <mo class=MathClass-bin>−</mo>
                        <msup>
                           <mrow>
                              <mi>r</mi>
                           </mrow>
                           <mrow>
                              <mi>′</mi>
                           </mrow>
                        </msup>
                        <mo class=MathClass-rel>|</mo>
                     </mrow>
                  </msup>
               </mrow>
               <mrow>
                  <mo class=MathClass-rel>|</mo>
                  <mi mathvariant="bold">r</mi>
                  <mo class=MathClass-bin>−</mo>
                  <msup>
                     <mrow>
                        <mi mathvariant="bold">r</mi>
                     </mrow>
                     <mrow>
                        <mi>′</mi>
                     </mrow>
                  </msup>
                  <mo class=MathClass-rel>|</mo>
               </mrow>
            </mfrac>
            <msub>
               <mrow>
                  <mi>v</mi>
               </mrow>
               <mrow>
                  <mi>n</mi>
               </mrow>
            </msub>
            <mo class=MathClass-open stretchy=false>(</mo>
            <msup>
               <mrow>
                  <mi>r</mi>
               </mrow>
               <mrow>
                  <mi>′</mi>
               </mrow>
            </msup>
            <mo class=MathClass-close stretchy=false>)</mo>
            <mi mathvariant=italic>dS</mi>
         </math>
      <td class=eq-no>(1)
</table>
<p>
   This integral is known as the Huygens-Rayleigh integral. It follows directly from Green’s theorem without volume sources with
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>G</mi>
      <mo class=MathClass-open stretchy=false>(</mo>
      <mi>r</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>ω</mi>
      <mo class=MathClass-close stretchy=false>)</mo>
      <mo class=MathClass-rel>=</mo>
      <mfrac>
         <mrow>
            <msup>
               <mrow>
                  <mi>e</mi>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>−</mo>
                  <mi mathvariant=italic>jkr</mi>
               </mrow>
            </msup>
         </mrow>
         <mrow>
            <mn>4</mn>
            <mi mathvariant=italic>πr</mi>
         </mrow>
      </mfrac>
   </math>.
<h4 class=subsectionHead>Fraunhofer approximation</h4>
<p>
   Far from the vibrating surface one can carry out a Taylor expansion of
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mo class=MathClass-rel>|</mo>
      <mi mathvariant="bold">r</mi>
      <mo class=MathClass-bin>−</mo>
      <msup>
         <mrow>
            <mi mathvariant="bold">r</mi>
         </mrow>
         <mrow>
            <mi>′</mi>
         </mrow>
      </msup>
      <mo class=MathClass-rel>|</mo>
   </math>
   at first order i.e.
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mo class=MathClass-rel>|</mo>
      <mi mathvariant="bold">r</mi>
      <mo class=MathClass-bin>−</mo>
      <msup>
         <mrow>
            <mi mathvariant="bold">r</mi>
         </mrow>
         <mrow>
            <mi>′</mi>
         </mrow>
      </msup>
      <mo class=MathClass-rel>|</mo>
      <mo class=MathClass-rel>≈</mo>
      <mi>r</mi>
      <mo class=MathClass-bin>−</mo>
      <msup>
         <mrow>
            <mi mathvariant="bold">r</mi>
         </mrow>
         <mrow>
            <mi>′</mi>
         </mrow>
      </msup>
      <mo class=MathClass-bin>⋅</mo>
      <mi mathvariant="bold">r</mi>
      <mo class=MathClass-bin>∕</mo>
      <mi>r</mi>
   </math>. The Huygens-Rayleigh integral then becomes
<table class=equation>
   <tr>
      <td style="font-size: 13px;">
         <math display=block xmlns=http://www.w3.org/1998/Math/MathML class=equation>
            <mi>p</mi>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi>r</mi>
            <mo class=MathClass-close stretchy=false>)</mo>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mi mathvariant=italic>jk</mi>
                  <msub>
                     <mrow>
                        <mi>ρ</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
                  <mi>c</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
                  <mi>π</mi>
               </mrow>
            </mfrac>
            <mfrac>
               <mrow>
                  <msup>
                     <mrow>
                        <mi>e</mi>
                     </mrow>
                     <mrow>
                        <mo class=MathClass-bin>−</mo>
                        <mi mathvariant=italic>jkr</mi>
                     </mrow>
                  </msup>
               </mrow>
               <mrow>
                  <mi>r</mi>
               </mrow>
            </mfrac>
            <msub>
               <mrow>
                  <mo>∬</mo>
               </mrow>
               <mrow>
                  <mi>S</mi>
               </mrow>
            </msub>
            <msup>
               <mrow>
                  <mi>e</mi>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>+</mo>
                  <mi mathvariant=italic >j</mi>
                  <mi mathvariant="bold">k</mi>
                  <mo class=MathClass-bin>⋅</mo>
                  <msup>
                     <mrow>
                        <mi mathvariant="bold">r</mi>
                     </mrow>
                     <mrow>
                        <mi>′</mi>
                     </mrow>
                  </msup>
               </mrow>
            </msup>
            <msub>
               <mrow>
                  <mi>v</mi>
               </mrow>
               <mrow>
                  <mi>n</mi>
               </mrow>
            </msub>
            <mo class=MathClass-open stretchy=false>(</mo>
            <msup>
               <mrow>
                  <mi>r</mi>
               </mrow>
               <mrow>
                  <mi>′</mi>
               </mrow>
            </msup>
            <mo class=MathClass-close stretchy=false>)</mo>
            <mi mathvariant=italic>dS</mi>
         </math>
      <td class=eq-no>(2)
</table>
<p>
   with
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi mathvariant="bold">k</mi>
      <mo class=MathClass-rel>=</mo>
      <mi mathvariant=italic>k</mi>
      <mi mathvariant=bold>r</mi>
      <mo class=MathClass-bin>∕</mo>
      <mi>r</mi>
   </math>
   The integral
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>V</mi>
         </mrow>
         <mrow>
            <mi>n</mi>
         </mrow>
      </msub>
      <mo class=MathClass-open>(</mo>
      <mi mathvariant="bold">k</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-rel>=</mo>
      <msub>
         <mrow>
            <mi class=MathClass-op>∬</mi>
            <mo>⁡</mo>
         </mrow>
         <mrow>
            <mi>S</mi>
         </mrow>
      </msub>
      <msub>
         <mrow>
            <mi>v</mi>
         </mrow>
         <mrow>
            <mi>n</mi>
         </mrow>
      </msub>
      <mo class=MathClass-open>(</mo>
      <msup>
         <mrow>
            <mi>r</mi>
         </mrow>
         <mrow>
            <mi>′</mi>
         </mrow>
      </msup>
      <mo class=MathClass-close>)</mo>
      <msup>
         <mrow>
            <mi>e</mi>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>+</mo>
            <mi mathvariant=italic>j</mi>
            <mi mathvariant="bold">k</mi>
            <mo class=MathClass-bin>⋅</mo>
            <msup>
               <mrow>
                  <mi mathvariant="bold">r</mi>
               </mrow>
               <mrow>
                  <mi>′</mi>
               </mrow>
            </msup>
         </mrow>
      </msup>
      <mi mathvariant=italic>dS</mi>
   </math>
   is the spatial Fourier transform of the normal speed of the surface. The far field pressure is then simply given by
<table class=equation>
   <tr>
      <td>
         <math display=block xmlns=http://www.w3.org/1998/Math/MathML class=equation>
            <mi>p</mi>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi>r</mi>
            <mo class=MathClass-close stretchy=false>)</mo>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mi mathvariant=italic>jk</mi>
                  <msub>
                     <mrow>
                        <mi>ρ</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
                  <mi>c</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
                  <mi>π</mi>
               </mrow>
            </mfrac>
            <mfrac>
               <mrow>
                  <msup>
                     <mrow>
                        <mi>e</mi>
                     </mrow>
                     <mrow>
                        <mo class=MathClass-bin>−</mo>
                        <mi mathvariant=italic>jkr</mi>
                     </mrow>
                  </msup>
               </mrow>
               <mrow>
                  <mi>r</mi>
               </mrow>
            </mfrac>
            <msub>
               <mrow>
                  <mi>V</mi>
               </mrow>
               <mrow>
                  <mi>n</mi>
               </mrow>
            </msub>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi mathvariant="bold">k</mi>
            <mo class=MathClass-close stretchy=false>)</mo>
         </math>
      <td class=eq-no>(3)
</table>
<p>
   Practically
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>V</mi>
         </mrow>
         <mrow>
            <mi>n</mi>
         </mrow>
      </msub>
      <mo class=MathClass-open>(</mo>
      <mi mathvariant="bold">k</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   is calculated in cylindrical coordinates by considering that the surface is in the plane
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msup>
         <mrow>
            <mi>z</mi>
         </mrow>
         <mrow>
            <mi>′</mi>
         </mrow>
      </msup>
      <mo class=MathClass-rel>=</mo>
      <mn>0</mn>
   </math> (see <a href="#" data-img-src=${config.ASSETS_URL}/01/16/theory/figure-1.svg data-img-alt="Figure 1. Oscillating circular piston.">Figure 1</a>).
   <figure class=figure>
<p><img src=${config.ASSETS_URL}/01/16/theory/figure-1.svg alt="Figure 1. Oscillating circular piston."><figcaption class=caption>Figure 1. Oscillating circular piston.</figcaption></figure>
<p>
   Setting
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msup>
         <mrow>
            <mi>w</mi>
         </mrow>
         <mrow>
            <mi>′</mi>
         </mrow>
      </msup>
      <mo class=MathClass-rel>=</mo>
      <msqrt>
         <mrow>
            <msup>
               <mrow>
                  <mi>x</mi>
               </mrow>
               <mrow>
                  <mi>′</mi>
                  <mn>2</mn>
               </mrow>
            </msup>
            <mo class=MathClass-bin>+</mo>
            <msup>
               <mrow>
                  <mi>y</mi>
               </mrow>
               <mrow>
                  <mi>′</mi>
                  <mn>2</mn>
               </mrow>
            </msup>
         </mrow>
      </msqrt>
   </math>, we have
<table class=equation>
   <tr>
      <td style="font-size: 8px;">
         <math display=block xmlns=http://www.w3.org/1998/Math/MathML class=equation>
            <mi mathvariant="bold">k</mi>
            <mo class=MathClass-bin>⋅</mo>
            <msup>
               <mrow>
                  <mi mathvariant="bold">r</mi>
               </mrow>
               <mrow>
                  <mi>′</mi>
               </mrow>
            </msup>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mi mathvariant=italic>k</mi>
                  <mi mathvariant=bold>r</mi>
                  <mo class=MathClass-bin>⋅</mo>
                  <msup>
                     <mrow>
                        <mi mathvariant="bold">r</mi>
                     </mrow>
                     <mrow>
                        <mi>′</mi>
                     </mrow>
                  </msup>
               </mrow>
               <mrow>
                  <mi>r</mi>
               </mrow>
            </mfrac>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mi>k</mi>
               </mrow>
               <mrow>
                  <mi>r</mi>
               </mrow>
            </mfrac>
            <mo class=MathClass-open>(</mo>
            <mi>w</mi>
            <mi class=qopname>cos</mi>
            <mo>⁡</mo>
            <mi>ϕ</mi>
            <mo class=MathClass-punc>,</mo>
            <mi>w</mi>
            <mi class=qopname>sin</mi>
            <mo>⁡</mo>
            <mi>ϕ</mi>
            <mo class=MathClass-punc>,</mo>
            <mi>z</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-bin>⋅</mo>
            <mrow>
               <mo form=prefix fence=true>(</mo>
               <mrow>
                  <mtable style="" equalcolumns=false columnlines="none none none none none none none none none" equalrows=false align=axis class=array>
                     <mtr>
                        <mtd class=array columnalign=center>
                           <msup>
                              <mrow>
                                 <mi>w</mi>
                              </mrow>
                              <mrow>
                                 <mi>′</mi>
                              </mrow>
                           </msup>
                           <mi class=qopname>cos</mi>
                           <mo>⁡</mo>
                           <msup>
                              <mrow>
                                 <mi>ϕ</mi>
                              </mrow>
                              <mrow>
                                 <mi>′</mi>
                              </mrow>
                           </msup>
                        </mtd>
                     </mtr>
                     <mtr>
                        <mtd class=array columnalign=center>
                           <msup>
                              <mrow>
                                 <mi>w</mi>
                              </mrow>
                              <mrow>
                                 <mi>′</mi>
                              </mrow>
                           </msup>
                           <mi class=qopname>sin</mi>
                           <mo>⁡</mo>
                           <msup>
                              <mrow>
                                 <mi>ϕ</mi>
                              </mrow>
                              <mrow>
                                 <mi>′</mi>
                              </mrow>
                           </msup>
                        </mtd>
                     </mtr>
                     <mtr>
                        <mtd class=array columnalign=center>
                           <mn>0</mn>
                        </mtd>
                     </mtr>
                  </mtable>
               </mrow>
               <mo form=postfix fence=true>)</mo>
            </mrow>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mi>k</mi>
               </mrow>
               <mrow>
                  <mi>r</mi>
               </mrow>
            </mfrac>
            <mi>w</mi>
            <msup>
               <mrow>
                  <mi>w</mi>
               </mrow>
               <mrow>
                  <mi>′</mi>
               </mrow>
            </msup>
            <mrow>
               <mo form=prefix fence=true>(</mo>
               <mrow>
                  <mi class=qopname>cos</mi>
                  <mo>⁡</mo>
                  <mi>ϕ</mi>
                  <mi class=qopname>cos</mi>
                  <mo>⁡</mo>
                  <msup>
                     <mrow>
                        <mi>ϕ</mi>
                     </mrow>
                     <mrow>
                        <mi>′</mi>
                     </mrow>
                  </msup>
                  <mo class=MathClass-bin>+</mo>
                  <mi class=qopname>sin</mi>
                  <mo>⁡</mo>
                  <mi>ϕ</mi>
                  <mi class=qopname>sin</mi>
                  <mo>⁡</mo>
                  <msup>
                     <mrow>
                        <mi>ϕ</mi>
                     </mrow>
                     <mrow>
                        <mi>′</mi>
                     </mrow>
                  </msup>
               </mrow>
               <mo form=postfix fence=true>)</mo>
            </mrow>
         </math>
      <td class=eq-no>(4)
</table>
<p>
   knowing that
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mfrac>
         <mrow>
            <mi>w</mi>
         </mrow>
         <mrow>
            <mi>r</mi>
         </mrow>
      </mfrac>
      <mo class=MathClass-rel>=</mo>
      <mi class=qopname>sin</mi>
      <mo>⁡</mo>
      <mi>𝜃</mi>
   </math>, we have
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi mathvariant="bold">k</mi>
      <mo class=MathClass-bin>⋅</mo>
      <msup>
         <mrow>
            <mi mathvariant="bold">r</mi>
         </mrow>
         <mrow>
            <mi>′</mi>
         </mrow>
      </msup>
      <mo class=MathClass-rel>=</mo>
      <mi>k</mi>
      <msup>
         <mrow>
            <mi>w</mi>
         </mrow>
         <mrow>
            <mi>′</mi>
         </mrow>
      </msup>
      <mi class=qopname>sin</mi>
      <mo>⁡</mo>
      <mi>𝜃</mi>
      <mi class=qopname>cos</mi>
      <mo>⁡</mo>
      <mo class=MathClass-open>(</mo>
      <mi>ϕ</mi>
      <mo class=MathClass-bin>−</mo>
      <msup>
         <mrow>
            <mi>ϕ</mi>
         </mrow>
         <mrow>
            <mi>′</mi></mrow></msup><mo>).</mo></math> Finally, we get
<table class=equation>
   <tr>
      <td style="font-size: 8px;">
         <math display=block xmlns=http://www.w3.org/1998/Math/MathML class=equation>
            <mi>p</mi>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi>r</mi>
            <mo class=MathClass-close stretchy=false>)</mo>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mi mathvariant=italic>jk</mi>
                  <msub>
                     <mrow>
                        <mi>ρ</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
                  <mi>c</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
                  <mi>π</mi>
               </mrow>
            </mfrac>
            <mfrac>
               <mrow>
                  <msup>
                     <mrow>
                        <mi>e</mi>
                     </mrow>
                     <mrow>
                        <mo class=MathClass-bin>−</mo>
                        <mi mathvariant=italic>jkr</mi>
                     </mrow>
                  </msup>
               </mrow>
               <mrow>
                  <mi>r</mi>
               </mrow>
            </mfrac>
            <msubsup>
               <mrow>
                  <mo>∫</mo>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
               <mrow>
                  <mn>2</mn>
                  <mi>π</mi>
               </mrow>
            </msubsup>
            <mstyle class=text>
               <mtext>d</mtext>
            </mstyle>
            <msup>
               <mrow>
                  <mi>ϕ</mi>
               </mrow>
               <mrow>
                  <mi>′</mi>
               </mrow>
            </msup>
            <msubsup>
               <mrow>
                  <mo>∫</mo>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
               <mrow>
                  <mi>R</mi>
                  <mo class=MathClass-open>(</mo>
                  <msup>
                     <mrow>
                        <mi>ϕ</mi>
                     </mrow>
                     <mrow>
                        <mi>′</mi>
                     </mrow>
                  </msup>
                  <mo class=MathClass-close>)</mo>
               </mrow>
            </msubsup>
            <msup>
               <mrow>
                  <mi>e</mi>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>+</mo>
                  <mi mathvariant=italic>jk</mi>
                  <msup>
                     <mrow>
                        <mi>w</mi>
                     </mrow>
                     <mrow>
                        <mi>′</mi>
                     </mrow>
                  </msup>
                  <mi class=qopname>sin</mi>
                  <mo>⁡</mo>
                  <mi>𝜃</mi>
                  <mi class=qopname>cos</mi>
                  <mo>⁡</mo>
                  <mo class=MathClass-open>(</mo>
                  <mi>ϕ</mi>
                  <mo class=MathClass-bin>−</mo>
                  <msup>
                     <mrow>
                        <mi>ϕ</mi>
                     </mrow>
                     <mrow>
                        <mi>′</mi>
                     </mrow>
                  </msup>
                  <mo class=MathClass-close>)</mo>
               </mrow>
            </msup>
            <msub>
               <mrow>
                  <mi>v</mi>
               </mrow>
               <mrow>
                  <mi>n</mi>
               </mrow>
            </msub>
            <mo class=MathClass-open stretchy=false>(</mo>
            <msup>
               <mrow>
                  <mi mathvariant="bold">r</mi>
               </mrow>
               <mrow>
                  <mi>′</mi>
               </mrow>
            </msup>
            <mo class=MathClass-close stretchy=false>)</mo>
            <msup>
               <mrow>
                  <mi>w</mi>
               </mrow>
               <mrow>
                  <mi>′</mi>
               </mrow>
            </msup>
            <mstyle class=text>
               <mtext>d’</mtext>
            </mstyle>
            <msup>
               <mrow>
                  <mi>w</mi>
               </mrow>
               <mrow>
                  <mi>′</mi>
               </mrow>
            </msup>
         </math>
      <td class=eq-no>(5)
</table>
<p>This result is due to Lord Rayleigh.
<h4 class=subsectionHead>Circular piston in an infinite screen</h4>
<h5 class=subsubsectionHead>Far field pressure</h5>
<p>
   We calculate here the radiation of the plane piston in the far field using the Fraunhofer approximation. The surface is a disk with radius
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>R</mi>
   </math>
   and center
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>O</mi>
   </math>
   animated with a speed
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>v</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
   </math>. So we have
<table class=equation>
   <tr>
      <td style="font-size: 9px;">
         <math display=block xmlns=http://www.w3.org/1998/Math/MathML class=equation>
            <mi>p</mi>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi>r</mi>
            <mo class=MathClass-close stretchy=false>)</mo>
            <mo class=MathClass-rel>=</mo>
            <mi mathvariant=italic>jk</mi>
            <msub>
               <mrow>
                  <mi>ρ</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
            <mi>c</mi>
            <msub>
               <mrow>
                  <mi>v</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
            <mfrac>
               <mrow>
                  <msup>
                     <mrow>
                        <mi>e</mi>
                     </mrow>
                     <mrow>
                        <mo class=MathClass-bin>−</mo>
                        <mi mathvariant=italic>jkr</mi>
                     </mrow>
                  </msup>
               </mrow>
               <mrow>
                  <mn>2</mn>
                  <mi mathvariant=italic>πr</mi>
               </mrow>
            </mfrac>
            <msubsup>
               <mrow>
                  <mo>∫</mo>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
               <mrow>
                  <mn>2</mn>
                  <mi>π</mi>
               </mrow>
            </msubsup>
            <mstyle class=text>
               <mtext>d</mtext>
            </mstyle>
            <msup>
               <mrow>
                  <mi>ϕ</mi>
               </mrow>
               <mrow>
                  <mi>′</mi>
               </mrow>
            </msup>
            <msubsup>
               <mrow>
                  <mo>∫</mo>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
               <mrow>
                  <mi>R</mi>
                  <mo class=MathClass-open>(</mo>
                  <msup>
                     <mrow>
                        <mi>ϕ</mi>
                     </mrow>
                     <mrow>
                        <mi>′</mi>
                     </mrow>
                  </msup>
                  <mo class=MathClass-close>)</mo>
               </mrow>
            </msubsup>
            <msup>
               <mrow>
                  <mi>e</mi>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>+</mo>
                  <mi mathvariant=italic>jk</mi>
                  <msup>
                     <mrow>
                        <mi>w</mi>
                     </mrow>
                     <mrow>
                        <mi>′</mi>
                     </mrow>
                  </msup>
                  <mi class=qopname>sin</mi>
                  <mo>⁡</mo>
                  <mi>𝜃</mi>
                  <mi class=qopname>cos</mi>
                  <mo>⁡</mo>
                  <mo class=MathClass-open>(</mo>
                  <mi>ϕ</mi>
                  <mo class=MathClass-bin>−</mo>
                  <msup>
                     <mrow>
                        <mi>ϕ</mi>
                     </mrow>
                     <mrow>
                        <mi>′</mi>
                     </mrow>
                  </msup>
                  <mo class=MathClass-close>)</mo>
               </mrow>
            </msup>
            <msup>
               <mrow>
                  <mi>w</mi>
               </mrow>
               <mrow>
                  <mi>′</mi>
               </mrow>
            </msup>
            <mstyle class=text>
               <mtext>d’</mtext>
            </mstyle>
            <msup>
               <mrow>
                  <mi>w</mi>
               </mrow>
               <mrow>
                  <mi>′</mi>
               </mrow>
            </msup>
         </math>
      <td class=eq-no>(6)
</table>
<p>
   which can still be written, since the result does not depend on
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>ϕ</mi>
   </math>,
<table class=equation>
   <tr>
      <td style="font-size: 10px;">
         <math display=block xmlns=http://www.w3.org/1998/Math/MathML class=equation>
            <mi>p</mi>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi>r</mi>
            <mo class=MathClass-close stretchy=false>)</mo>
            <mo class=MathClass-rel>=</mo>
            <mi mathvariant=italic>jk</mi>
            <msub>
               <mrow>
                  <mi>ρ</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
            <mi>c</mi>
            <msub>
               <mrow>
                  <mi>v</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
            <mfrac>
               <mrow>
                  <msup>
                     <mrow>
                        <mi>e</mi>
                     </mrow>
                     <mrow>
                        <mo class=MathClass-bin>−</mo>
                        <mi mathvariant=italic>jkr</mi>
                     </mrow>
                  </msup>
               </mrow>
               <mrow>
                  <mn>2</mn>
                  <mi mathvariant=italic>πr</mi>
               </mrow>
            </mfrac>
            <msubsup>
               <mrow>
                  <mo>∫</mo>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
               <mrow>
                  <mi>R</mi>
               </mrow>
            </msubsup>
            <msup>
               <mrow>
                  <mi>w</mi>
               </mrow>
               <mrow>
                  <mi>′</mi>
               </mrow>
            </msup>
            <mrow>
               <mo form=prefix fence=true>(</mo>
               <mrow>
                  <msubsup>
                     <mrow>
                        <mo>∫</mo>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                        <mi>π</mi>
                     </mrow>
                  </msubsup>
                  <msup>
                     <mrow>
                        <mi>e</mi>
                     </mrow>
                     <mrow>
                        <mo class=MathClass-bin>+</mo>
                        <mi mathvariant=italic>jk</mi>
                        <msup>
                           <mrow>
                              <mi>w</mi>
                           </mrow>
                           <mrow>
                              <mi>′</mi>
                           </mrow>
                        </msup>
                        <mi class=qopname>sin</mi>
                        <mo>⁡</mo>
                        <mi>𝜃</mi>
                        <mi class=qopname>cos</mi>
                        <mo>⁡</mo>
                        <msup>
                           <mrow>
                              <mi>ϕ</mi>
                           </mrow>
                           <mrow>
                              <mi>′</mi>
                           </mrow>
                        </msup>
                     </mrow>
                  </msup>
               </mrow>
               <mo form=postfix fence=true>)</mo>
            </mrow>
            <mstyle class=text>
               <mtext>d</mtext>
            </mstyle>
            <msup>
               <mrow>
                  <mi>ϕ</mi>
               </mrow>
               <mrow>
                  <mi>′</mi>
               </mrow>
            </msup>
            <mstyle class=text>
               <mtext>d</mtext>
            </mstyle>
            <msup>
               <mrow>
                  <mi>w</mi>
               </mrow>
               <mrow>
                  <mi>′</mi>
               </mrow>
            </msup>
         </math>
      <td class=eq-no>(7)
</table>
<p>
   Considering that
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>J</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
      <mo class=MathClass-open>(</mo>
      <mi>X</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-rel>=</mo>
      <msubsup>
         <mrow>
            <mi class=MathClass-op>∫</mi>
            <mo>⁡</mo>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
         <mrow>
            <mn>2</mn>
            <mi>π</mi>
         </mrow>
      </msubsup>
      <msup>
         <mrow>
            <mi>e</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>jX</mi>
            <mi class=qopname>cos</mi>
            <mo>⁡</mo>
            <mi>ϕ</mi>
         </mrow>
      </msup>
      <mstyle class=text>
         <mtext>d</mtext>
      </mstyle>
      <mi>ϕ</mi>
   </math>
   and setting
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>X</mi>
      <mo class=MathClass-rel>=</mo>
      <mi>k</mi>
      <mi class=qopname>sin</mi>
      <mo>⁡</mo>
      <mi>𝜃</mi>
   </math>, we obtain
<table class=equation>
   <tr>
      <td style="font-size: 11px;">
         <math display=block xmlns=http://www.w3.org/1998/Math/MathML class=equation>
            <mi>p</mi>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi>r</mi>
            <mo class=MathClass-close stretchy=false>)</mo>
            <mo class=MathClass-rel>=</mo>
            <mi mathvariant=italic>jk</mi>
            <msub>
               <mrow>
                  <mi>ρ</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
            <mi>c</mi>
            <msub>
               <mrow>
                  <mi>v</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
            <mfrac>
               <mrow>
                  <msup>
                     <mrow>
                        <mi>e</mi>
                     </mrow>
                     <mrow>
                        <mo class=MathClass-bin>−</mo>
                        <mi mathvariant=italic>jkr</mi>
                     </mrow>
                  </msup>
               </mrow>
               <mrow>
                  <mn>2</mn>
                  <mi mathvariant=italic>πr</mi>
               </mrow>
            </mfrac>
            <mfrac>
               <mrow>
                  <mn>1</mn>
               </mrow>
               <mrow>
                  <msup>
                     <mrow>
                        <mi>k</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
                  <msup>
                     <mrow>
                        <mi class=qopname>sin</mi>
                        <mo>⁡</mo>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
                  <mi>𝜃</mi>
               </mrow>
            </mfrac>
            <msubsup>
               <mrow>
                  <mo>∫</mo>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>kR</mi>
                  <mi class=qopname>sin</mi>
                  <mo>⁡</mo>
                  <mi>𝜃</mi>
               </mrow>
            </msubsup>
            <msub>
               <mrow>
                  <mi>J</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi>X</mi>
            <mo class=MathClass-close stretchy=false>)</mo>
            <mi>X</mi>
            <mstyle class=text>
               <mtext>d</mtext>
            </mstyle>
            <mi>X</mi>
         </math>
      <td class=eq-no>(8)
</table>
<p>
   As
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>X</mi>
      <msub>
         <mrow>
            <mi>J</mi>
         </mrow>
         <mrow>
            <mn>1</mn>
         </mrow>
      </msub>
      <mo class=MathClass-open stretchy=false>(</mo>
      <mi>X</mi>
      <mo class=MathClass-close stretchy=false>)</mo>
      <mo class=MathClass-rel>=</mo>
      <msubsup>
         <mrow>
            <mi class=MathClass-op>∫</mi>
            <mo>⁡</mo>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
         <mrow>
            <mi>X</mi>
         </mrow>
      </msubsup>
      <msub>
         <mrow>
            <mi>J</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
      <mo class=MathClass-open stretchy=false>(</mo>
      <mi>X</mi>
      <mo class=MathClass-close stretchy=false>)</mo>
      <mi>X</mi>
      <mstyle class=text>
         <mtext>d</mtext>
      </mstyle>
      <mi>X</mi>
   </math>, we finally get
<table class=equation>
   <tr>
      <td style="font-size: 14px;">
         <math display=block xmlns=http://www.w3.org/1998/Math/MathML class=equation>
            <mi>p</mi>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi>r</mi>
            <mo class=MathClass-close stretchy=false>)</mo>
            <mo class=MathClass-rel>=</mo>
            <mi mathvariant=italic>jk</mi>
            <msup>
               <mrow>
                  <mi>R</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msup>
            <msub>
               <mrow>
                  <mi>ρ</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
            <mi>c</mi>
            <msub>
               <mrow>
                  <mi>v</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
            <mfrac>
               <mrow>
                  <msup>
                     <mrow>
                        <mi>e</mi>
                     </mrow>
                     <mrow>
                        <mo class=MathClass-bin>−</mo>
                        <mi mathvariant=italic>jkr</mi>
                     </mrow>
                  </msup>
               </mrow>
               <mrow>
                  <mn>2</mn>
                  <mi mathvariant=italic>πr</mi>
               </mrow>
            </mfrac>
            <mfrac>
               <mrow>
                  <mn>2</mn>
                  <msub>
                     <mrow>
                        <mi>J</mi>
                     </mrow>
                     <mrow>
                        <mn>1</mn>
                     </mrow>
                  </msub>
                  <mo class=MathClass-open>(</mo>
                  <mi mathvariant=italic>kR</mi>
                  <mi class=qopname>sin</mi>
                  <mo>⁡</mo>
                  <mi>𝜃</mi>
                  <mo class=MathClass-close>)</mo>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>kR</mi>
                  <mi class=qopname>sin</mi>
                  <mo>⁡</mo>
                  <mi>𝜃</mi>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(9)
</table>
<p>
   Setting
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>Q</mi>
      <mo class=MathClass-rel>=</mo>
      <mi>π</mi>
      <msup>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msup>
      <msub>
         <mrow>
            <mi>v</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>D</mi>
      <mo class=MathClass-open stretchy=false>(</mo>
      <mi>𝜃</mi>
      <mo class=MathClass-close stretchy=false>)</mo>
      <mo class=MathClass-rel>=</mo>
      <mfrac>
         <mrow>
            <mn>2</mn>
            <msub>
               <mrow>
                  <mi>J</mi>
               </mrow>
               <mrow>
                  <mn>1</mn>
               </mrow>
            </msub>
            <mo class=MathClass-open>(</mo>
            <mi mathvariant=italic>kR</mi>
            <mi class=qopname>sin</mi>
            <mo>⁡</mo>
            <mi>𝜃</mi>
            <mo class=MathClass-close>)</mo>
         </mrow>
         <mrow>
            <mi mathvariant=italic>kR</mi>
            <mi class=qopname>sin</mi>
            <mo>⁡</mo>
            <mi>𝜃</mi>
         </mrow>
      </mfrac>
   </math>
   (NB:
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>J</mi>
         </mrow>
         <mrow>
            <mn>1</mn>
         </mrow>
      </msub>
      <mo class=MathClass-open>(</mo>
      <mi>𝜖</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-rel>≈</mo>
      <mi>𝜖</mi>
      <mo class=MathClass-bin>∕</mo>
      <mn>2</mn>
   </math>), the pressure can be written
<table class=equation>
   <tr>
      <td>
         <math display=block xmlns=http://www.w3.org/1998/Math/MathML class=equation>
            <mi>p</mi>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi>r</mi>
            <mo class=MathClass-close stretchy=false>)</mo>
            <mo class=MathClass-rel>=</mo>
            <mn>2</mn>
            <mi>j</mi>
            <msub>
               <mrow>
                  <mi>ρ</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
            <mi mathvariant=italic>ωQ</mi>
            <mfrac>
               <mrow>
                  <msup>
                     <mrow>
                        <mi>e</mi>
                     </mrow>
                     <mrow>
                        <mo class=MathClass-bin>−</mo>
                        <mi mathvariant=italic>jkr</mi>
                     </mrow>
                  </msup>
               </mrow>
               <mrow>
                  <mn>4</mn>
                  <mi mathvariant=italic>πr</mi>
               </mrow>
            </mfrac>
            <mi>D</mi>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi>𝜃</mi>
            <mo class=MathClass-close stretchy=false>)</mo>
         </math>
      <td class=eq-no>(10)
</table>
<p>
   We recognize here the expression of a point source in a plane (hence the factor 2) with a directivity factor
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>D</mi>
      <mo class=MathClass-open>(</mo>
      <mi>𝜃</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   equal to 1 on the axis. At low frequencies,
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi mathvariant=italic>kR</mi>
      <mo class=MathClass-rel><</mo>
      <mo class=MathClass-rel><</mo>
      <mn>1</mn>
   </math>, the source is omnidirectional. When,
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi mathvariant=italic>kR</mi>
      <mo class=MathClass-rel>=</mo>
      <mn>3</mn>
      <mo class=MathClass-punc>.</mo>
      <mn>83</mn>
   </math>
   we see a first lobe appear (see <a href="#" data-img-src=${config.ASSETS_URL}/01/16/theory/figure-2.svg data-img-alt="Figure 2. Plane piston directivity diagrams in dB (reference 60dB) for <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><mi mathvariant=italic>kR</mi><mo class=MathClass-rel>=</mo><mn>1</mn><mo class=MathClass-punc>;</mo><mn>2</mn><mo class=MathClass-punc>;</mo><mn>4</mn><mo class=MathClass-punc>;</mo><mn>8</mn><mo class=MathClass-punc>;</mo><mn>16</mn></math>.">Figure 2</a>). At high frequency the number of lobes is roughly equal to twice the number of wavelengths on the source.
   <figure class=figure>
<p><img src=${config.ASSETS_URL}/01/16/theory/figure-2.svg alt="Figure 2. Plane piston directivity diagrams in dB (reference 60dB) for <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><mi mathvariant=italic>kR</mi><mo class=MathClass-rel>=</mo><mn>1</mn><mo class=MathClass-punc>;</mo><mn>2</mn><mo class=MathClass-punc>;</mo><mn>4</mn><mo class=MathClass-punc>;</mo><mn>8</mn><mo class=MathClass-punc>;</mo><mn>16</mn></math>"><figcaption class=caption>Figure 2. Plane piston directivity diagrams in dB (reference 60 dB) for <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><mi mathvariant=italic>kR</mi><mo class=MathClass-rel>=</mo><mn>1</mn><mo class=MathClass-punc>;</mo><mn>2</mn><mo class=MathClass-punc>;</mo><mn>4</mn><mo class=MathClass-punc>;</mo><mn>8</mn><mo class=MathClass-punc>;</mo><mn>16</mn></math>.</figcaption></figure>
<h5 class=subsubsectionHead>Radiation impedance of the flat piston</h5>
<p>This impedance was calculated by Lord Rayleigh. The fairly difficult calculation leads to
<table class=equation>
   <tr>
      <td style="font-size: 12px;">
         <math display=block xmlns=http://www.w3.org/1998/Math/MathML class=equation>
            <mi>Z</mi>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mover accent=true>
                     <mrow>
                        <mi>p</mi>
                     </mrow>
                     <mo accent=true>¯</mo>
                  </mover>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>v</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>ρ</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
            <mi>c</mi>
            <mrow>
               <mo form=prefix fence=true>(</mo>
               <mrow>
                  <mn>1</mn>
                  <mo class=MathClass-bin>−</mo>
                  <mfrac>
                     <mrow>
                        <msub>
                           <mrow>
                              <mi>J</mi>
                           </mrow>
                           <mrow>
                              <mn>1</mn>
                           </mrow>
                        </msub>
                        <mo class=MathClass-open>(</mo>
                        <mn>2</mn>
                        <mi mathvariant=italic>kR</mi>
                        <mo class=MathClass-close>)</mo>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>kR</mi>
                     </mrow>
                  </mfrac>
                  <mo class=MathClass-bin>+</mo>
                  <mi>j</mi>
                  <mfrac>
                     <mrow>
                        <msub>
                           <mrow>
                              <mi>S</mi>
                           </mrow>
                           <mrow>
                              <mn>1</mn>
                           </mrow>
                        </msub>
                        <mo class=MathClass-open>(</mo>
                        <mn>2</mn>
                        <mi mathvariant=italic>kR</mi>
                        <mo class=MathClass-close>)</mo>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>kR</mi>
                     </mrow>
                  </mfrac>
               </mrow>
               <mo form=postfix fence=true>)</mo>
            </mrow>
         </math>
      <td class=eq-no>(11)
</table>
<p>
   where
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>S</mi>
         </mrow>
         <mrow>
            <mn>1</mn>
         </mrow>
      </msub>
   </math>
   is the Struve function, solution of an inhomogeneous Bessel equation. In low frequency, the radiation impedance is written
<table class=equation>
   <tr>
      <td>
         <math display=block xmlns=http://www.w3.org/1998/Math/MathML class=equation>
            <mi>Z</mi>
            <mo class=MathClass-rel>≈</mo>
            <msub>
               <mrow>
                  <mi>ρ</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
            <mi>c</mi>
            <mrow>
               <mo form=prefix fence=true>(</mo>
               <mrow>
                  <mfrac>
                     <mrow>
                        <mn>1</mn>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </mfrac>
                  <msup>
                     <mrow>
                        <mi>k</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
                  <msup>
                     <mrow>
                        <mi>R</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
                  <mo class=MathClass-bin>+</mo>
                  <mfrac>
                     <mrow>
                        <mn>8</mn>
                     </mrow>
                     <mrow>
                        <mn>3</mn>
                        <mi>π</mi>
                     </mrow>
                  </mfrac>
                  <mi mathvariant=italic>jkR</mi>
               </mrow>
               <mo form=postfix fence=true>)</mo>
            </mrow>
         </math>
      <td class=eq-no>(12)
</table>
<p>
   This expression shows that at low frequency the piston radiates like a point source in front of an infinite screen placed at a distance
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mn>8</mn>
      <mi>R</mi>
      <mo class=MathClass-bin>∕</mo>
      <mn>3</mn>
      <mi>π</mi>
      <mo class=MathClass-rel>≈</mo>
      <mn>0</mn>
      <mo class=MathClass-punc>.</mo>
      <mn>85</mn>
      <mi>R</mi>
   </math>
   from the screen. This expression is to be compared with the impedance of the pulsating sphere in infinite space:
<table class=equation>
   <tr>
      <td style="font-size: 8px;">
         <math display=block xmlns=http://www.w3.org/1998/Math/MathML class=equation>
            <msub>
               <mrow>
                  <mi>Z</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>sphere</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mn>1</mn>
               </mrow>
               <mrow>
                  <mn>4</mn>
               </mrow>
            </mfrac>
            <msub>
               <mrow>
                  <mi>ρ</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
            <mi>c</mi>
            <mrow>
               <mo form=prefix fence=true>(</mo>
               <mrow>
                  <mfrac>
                     <mrow>
                        <mi mathvariant=italic>jkR</mi>
                     </mrow>
                     <mrow>
                        <mn>1</mn>
                        <mo class=MathClass-bin>+</mo>
                        <msup>
                           <mrow>
                              <mi>k</mi>
                           </mrow>
                           <mrow>
                              <mn>2</mn>
                           </mrow>
                        </msup>
                        <msup>
                           <mrow>
                              <mi>R</mi>
                           </mrow>
                           <mrow>
                              <mn>2</mn>
                           </mrow>
                        </msup>
                     </mrow>
                  </mfrac>
                  <mo class=MathClass-bin>+</mo>
                  <mfrac>
                     <mrow>
                        <msup>
                           <mrow>
                              <mi>k</mi>
                           </mrow>
                           <mrow>
                              <mn>2</mn>
                           </mrow>
                        </msup>
                        <msup>
                           <mrow>
                              <mi>R</mi>
                           </mrow>
                           <mrow>
                              <mn>2</mn>
                           </mrow>
                        </msup>
                     </mrow>
                     <mrow>
                        <mn>1</mn>
                        <mo class=MathClass-bin>+</mo>
                        <msup>
                           <mrow>
                              <mi>k</mi>
                           </mrow>
                           <mrow>
                              <mn>2</mn>
                           </mrow>
                        </msup>
                        <msup>
                           <mrow>
                              <mi>R</mi>
                           </mrow>
                           <mrow>
                              <mn>2</mn>
                           </mrow>
                        </msup>
                     </mrow>
                  </mfrac>
               </mrow>
               <mo form=postfix fence=true>)</mo>
            </mrow>
            <mo class=MathClass-rel>≈</mo>
            <msub>
               <mrow>
                  <mi>ρ</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
            <mi>c</mi>
            <mrow>
               <mo form=prefix fence=true>(</mo>
               <mrow>
                  <mfrac>
                     <mrow>
                        <mn>1</mn>
                     </mrow>
                     <mrow>
                        <mn>4</mn>
                     </mrow>
                  </mfrac>
                  <msup>
                     <mrow>
                        <mi>k</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
                  <msup>
                     <mrow>
                        <mi>R</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
                  <mo class=MathClass-bin>+</mo>
                  <mi mathvariant=italic>jkR</mi>
               </mrow>
               <mo form=postfix fence=true>)</mo>
            </mrow>
         </math>
      <td class=eq-no>(13)
</table>
<p>At high frequencies the impedance of the piston becomes
<table class=equation>
   <tr>
      <td>
         <math display=block xmlns=http://www.w3.org/1998/Math/MathML class=equation>
            <mi>Z</mi>
            <mo class=MathClass-rel>≈</mo>
            <msub>
               <mrow>
                  <mi>ρ</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
            <mi>c</mi>
            <mrow>
               <mo form=prefix fence=true>(</mo>
               <mrow>
                  <mn>1</mn>
                  <mo class=MathClass-bin>+</mo>
                  <mi>j</mi>
                  <mfrac>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>πkR</mi>
                     </mrow>
                  </mfrac>
               </mrow>
               <mo form=postfix fence=true>)</mo>
            </mrow>
         </math>
      <td class=eq-no>(14)
</table>
<p>
   and all the energy is radiated because
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>ℜ</mi>
      <mo class=MathClass-open>(</mo>
      <mi>Z</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-rel>=</mo>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mi>c</mi>
         </mrow>
      </msub>
   </math>, with
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mi>c</mi>
         </mrow>
      </msub>
   </math>
   the characteristic impedance (see <a href="#" data-img-src=${config.ASSETS_URL}/01/15/theory/figure-3.svg data-img-alt="Figure 3. Plane piston reduced impedance. <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><msub><mrow><mi>Z</mi></mrow><mrow><mi>c</mi></mrow></msub></math> is the characteristic impedance.">Figure 3</a>).
   <figure class=figure>
<p><img src=${config.ASSETS_URL}/01/16/theory/figure-3.svg alt="Figure 3. Plane piston reduced impedance. <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><msub><mrow><mi>Z</mi></mrow><mrow><mi>c</mi></mrow></msub></math> is the characteristic impedance."><figcaption class=caption>Figure 3. Plane piston reduced impedance. <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><msub><mrow><mi>Z</mi></mrow><mrow><mi>c</mi></mrow></msub></math> is the characteristic impedance.</figcaption></figure>
<h3 class=sectionHead>References</h3>
<dl class=thebibliography>
  <dt>[1]
  <dd>M. Abramowitz and I.A. Stegun. Handbook of mathematical functions. Dover, 1965.
  <dt>[2]
  <dd>L.J. Beranek. Acoustics. The American Institute of Physics, New York, 1986.
  <dt>[3]
  <dd>M. Bruneau. Manuel d’Acoustique Fondamentale. Hermes, Paris, 1998.
  <dt>[4]
  <dd>A. Chaigne and J. Kergomard. Acoustics of Musical Instruments. Springer, New York, 2016.
  <dt>[5]
  <dd>A.D. Pierce. Acoustics: an Introduction to its Physical Principles and Applications. MCGraw-Hill Book Company, Inc., New York, 1981.
  <dt>[6]
  <dd>Lord Rayleigh. The Theory of Sound, volume 2. Macmillan, 2nd edition, 1896.
  <dt>[7]
  <dd>E. Skudrzyk. The Foundations of Acoustics—Basic Mathematics and Basic Acoustics. Springer–Verlag, 1971.
  <dt>[8]
  <dd>S. Temkin. Elements of Acoustics. Wiley, New York, 1981.
</dl>`;

export default theory;
